import { useEffect, useMemo, useState } from 'react';
import { fetchTenant } from '../lib/accountApi';
import { Dumbbell, ChevronLeft, ChevronRight, ChevronDown, LogOut, Clock, Plus, Check, Trash2, X, Search, Flame, Play, Share2, Sparkles, Target, Receipt } from 'lucide-react';
import confetti from 'canvas-confetti';
import type { MemberMe, MemberWeek, MemberWorkoutView, Exercise, Goal, MemberSubscription } from '@techsofit/api-types';
import { Button, Card, Input, Modal, Badge, Skeleton, cn } from '@techsofit/ui';
import { GlowUpStory } from '../components/GlowUpStory';
import { NotificationBell } from '../components/NotificationBell';
import { InviteFriend } from '../components/InviteFriend';
import { shareOrCopy } from '../lib/share';
import { useAsync } from '../lib/useAsync';
import { uid } from '../lib/uid';

function celebrate() {
  confetti({ particleCount: 90, spread: 70, origin: { y: 0.7 }, scalar: 0.9 });
}

/** Light haptic on tick (mobile) — a satisfying in-flow reward that never interrupts. */
function buzz(ms: number | number[] = 12) {
  if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
    try {
      navigator.vibrate(ms);
    } catch {
      /* unsupported — ignore */
    }
  }
}

/** Escalating encouragement as a member works through a workout. */
function momentumMessage(done: number, total: number, completed: boolean): string {
  if (total === 0) return '';
  if (completed) return 'Workout complete — nice work! 🎉';
  if (done === 0) return `${total} exercises`;
  if (done === total - 1) return 'Last one — finish strong! 🙌';
  if (done / total >= 0.5) return 'Halfway there — keep pushing! 🔥';
  return `${done}/${total} done — keep the momentum 💪`;
}
import { MemberLogin } from '../components/MemberLogin';
import { quoteOfDay } from '../lib/quotes';
import {
  getMemberMe,
  memberLogout,
  getMemberWeek,
  createSelfWorkout,
  deleteSelfWorkout,
  setMemberCompletion,
  getMemberExercises,
  getMemberExerciseFilters,
  getMemberStats,
  getMemberGoals,
  getMyGlowUp,
  shareMyGlowUp,
  getMySubscriptions,
} from '../lib/memberApi';
import { money, formatDate } from '../lib/format';

function isoDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}
function mondayOf(d: Date): Date {
  return addDays(d, -((d.getDay() + 6) % 7));
}
const DAY_FMT = new Intl.DateTimeFormat('en', { weekday: 'long', day: 'numeric', month: 'short' });

export function MemberPortalPage() {
  const [me, setMe] = useState<MemberMe | null | undefined>(undefined);

  useEffect(() => {
    getMemberMe()
      .then((m) => {
        // Already signed in — drop a stale one-tap token from the email link, if any.
        const url = new URL(window.location.href);
        if (url.searchParams.has('lt')) {
          url.searchParams.delete('lt');
          window.history.replaceState({}, '', url.pathname + (url.search || '') + url.hash);
        }
        setMe(m);
      })
      .catch(() => setMe(null));
  }, []);

  if (me === undefined) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-canvas p-4">
        <Skeleton className="h-40 w-full max-w-sm rounded-card" />
      </div>
    );
  }
  return me ? <MyWeek me={me} onLogout={() => setMe(null)} /> : <MemberLogin onAuthed={setMe} />;
}

function MyWeek({ me, onLogout }: { me: MemberMe; onLogout: () => void }) {
  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));
  const [data, setData] = useState<MemberWeek | null>(null);
  const [loading, setLoading] = useState(true);
  const [addFor, setAddFor] = useState<string | null>(null);
  const [tab, setTab] = useState<'plan' | 'progress'>('plan');
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [guestPassOn, setGuestPassOn] = useState(false);

  // Brand the portal with the gym's logo (from its subdomain), and learn if guest passes are on.
  useEffect(() => {
    fetchTenant()
      .then((t) => {
        setLogoUrl(t?.logoUrl ?? null);
        setGuestPassOn(!!t?.guestPassEnabled);
      })
      .catch(() => undefined);
  }, []);

  const from = isoDate(weekStart);
  const to = isoDate(addDays(weekStart, 6));

  function load() {
    setLoading(true);
    getMemberWeek(from, to)
      .then(setData)
      .finally(() => setLoading(false));
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [from, to]);

  const byDay = useMemo(() => {
    const map = new Map<string, MemberWorkoutView[]>();
    for (const w of data?.workouts ?? []) {
      const list = map.get(w.date) ?? [];
      list.push(w);
      map.set(w.date, list);
    }
    return map;
  }, [data]);

  const todayIso = isoDate(new Date());
  const isCurrentWeek = from === isoDate(mondayOf(new Date()));
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)).filter(
    (d) => !isCurrentWeek || isoDate(d) >= todayIso,
  );
  const first = me.fullName.split(/\s+/)[0];
  const quote = quoteOfDay();

  function patchWorkout(id: string, next: Partial<MemberWorkoutView>) {
    setData((prev) => prev && { ...prev, workouts: prev.workouts.map((w) => (w.id === id ? { ...w, ...next } : w)) });
  }

  function toggleExercise(w: MemberWorkoutView, index: number) {
    const nextExercises = w.exercises.map((e, i) => (i === index ? { ...e, done: !e.done } : e));
    const doneIdx = nextExercises.map((e, i) => (e.done ? i : -1)).filter((i) => i >= 0);
    const completed = doneIdx.length === nextExercises.length && nextExercises.length > 0;
    if (completed && !w.completed) {
      celebrate();
      buzz([14, 50, 28]);
    } else {
      buzz(12);
    }
    patchWorkout(w.id, { exercises: nextExercises, completed });
    setMemberCompletion({ kind: w.kind, refId: w.id, completedExercises: doneIdx, completed }).catch(load);
  }

  function markDone(w: MemberWorkoutView, completed: boolean) {
    const nextExercises = w.exercises.map((e) => ({ ...e, done: completed }));
    const doneIdx = completed ? nextExercises.map((_, i) => i) : [];
    if (completed && !w.completed) {
      celebrate();
      buzz([14, 50, 28]);
    }
    patchWorkout(w.id, { exercises: nextExercises, completed });
    setMemberCompletion({ kind: w.kind, refId: w.id, completedExercises: doneIdx, completed }).catch(load);
  }

  async function removeSelf(w: MemberWorkoutView) {
    if (!window.confirm(`Delete "${w.workoutName}"?`)) return;
    await deleteSelfWorkout(w.id).catch(() => undefined);
    load();
  }

  async function logout() {
    await memberLogout().catch(() => undefined);
    onLogout();
  }

  return (
    <div className="min-h-dvh bg-canvas">
      <header className="sticky top-0 z-10 border-b border-line bg-card/90 backdrop-blur">
        <div className="mx-auto flex max-w-2xl items-center justify-between p-4">
          <div className="flex min-w-0 items-center gap-3">
            {logoUrl && (
              <img
                src={logoUrl}
                alt=""
                className="size-10 shrink-0 rounded-control border border-line object-contain"
              />
            )}
            <div className="min-w-0">
              <p className="truncate text-sm text-muted">{me.gymName}</p>
              <p className="truncate font-display text-lg font-semibold text-ink">Hi {first} 👋</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <NotificationBell />
            <button onClick={logout} aria-label="Sign out" className="inline-flex size-touch items-center justify-center rounded-control text-muted hover:bg-surface hover:text-ink">
              <LogOut className="size-5" aria-hidden />
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-2xl p-4">
        <Card tone="dark" className="mb-4 p-4">
          <p className="text-eyebrow uppercase text-accent">Today</p>
          <p className="mt-1.5 font-display text-base leading-snug text-on-dark">“{quote.text}”</p>
          <p className="mt-1 text-xs text-on-dark-muted">— {quote.author}</p>
        </Card>

        <div className="mb-4 flex gap-1 border-b border-line">
          <TabBtn label="Plan" active={tab === 'plan'} onClick={() => setTab('plan')} />
          <TabBtn label="Progress" active={tab === 'progress'} onClick={() => setTab('progress')} />
        </div>

        {tab === 'progress' ? (
          <ProgressTab />
        ) : (
        <>
        {guestPassOn && <InviteFriend gymName={me.gymName} />}
        <div className="mb-4 flex items-center gap-2">
          <Button size="sm" variant="ghost" onClick={() => setWeekStart(addDays(weekStart, -7))} aria-label="Previous week">
            <ChevronLeft className="size-4" aria-hidden />
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setWeekStart(mondayOf(new Date()))}>
            This week
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setWeekStart(addDays(weekStart, 7))} aria-label="Next week">
            <ChevronRight className="size-4" aria-hidden />
          </Button>
        </div>

        {loading ? (
          <div className="flex flex-col gap-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-24 rounded-card" />
            ))}
          </div>
        ) : (
          <div className="flex flex-col gap-5">
            {days.map((d) => {
              const iso = isoDate(d);
              const list = byDay.get(iso) ?? [];
              const isToday = iso === todayIso;
              return (
                <div key={iso}>
                  <div className="mb-2 flex items-center justify-between">
                    <p className={cn('text-sm font-semibold', isToday ? 'text-ink' : 'text-ink-soft')}>
                      {DAY_FMT.format(d)} {isToday && <span className="text-accent-ink">· Today</span>}
                    </p>
                    <button
                      type="button"
                      onClick={() => setAddFor(iso)}
                      className="inline-flex items-center gap-1 text-sm font-medium text-ink-soft hover:text-ink"
                    >
                      <Plus className="size-4" aria-hidden /> Add
                    </button>
                  </div>
                  {list.length === 0 ? (
                    <div className="rounded-card border border-dashed border-line px-4 py-3 text-sm text-muted">
                      No workouts scheduled
                    </div>
                  ) : (
                    <div className="flex flex-col gap-3">
                      {list.map((w) => (
                        <WorkoutCard
                          key={w.id}
                          workout={w}
                          onToggleExercise={(i) => toggleExercise(w, i)}
                          onMarkDone={(c) => markDone(w, c)}
                          onDelete={w.kind === 'SELF' ? () => removeSelf(w) : undefined}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        </>
        )}
      </main>

      {addFor && <AddWorkoutModal date={addFor} onClose={() => setAddFor(null)} onSaved={() => { setAddFor(null); load(); }} />}
    </div>
  );
}

function TabBtn({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        '-mb-px border-b-2 px-4 py-2.5 text-sm font-medium transition-colors',
        active ? 'border-ink text-ink' : 'border-transparent text-muted hover:text-ink',
      )}
    >
      {label}
    </button>
  );
}

function ProgressTab() {
  const { data: stats } = useAsync(getMemberStats);
  const { data: goals } = useAsync(getMemberGoals);
  const { data: glow } = useAsync(getMyGlowUp);
  const [playing, setPlaying] = useState(false);

  const hasGlow = !!glow && (glow.weeksTracked > 0 || glow.goalsReached > 0);

  async function share() {
    if (!glow) return;
    const { url } = await shareMyGlowUp();
    await shareOrCopy(url, `${glow.memberName}'s progress at ${glow.gymName}`);
  }

  return (
    <div className="flex flex-col gap-5">
      <StreakHero streak={stats?.streakDays ?? 0} thisWeek={stats?.thisWeek ?? 0} completed={stats?.completed ?? 0} />
      <ActivityCalendar activeDays={stats?.activeDays ?? []} />
      <Milestones streak={stats?.streakDays ?? 0} completed={stats?.completed ?? 0} />

      {hasGlow ? (
        <Card tone="dark" className="p-5">
          <p className="text-eyebrow uppercase text-accent">Your Glow Up</p>
          <p className="mt-2 font-display text-2xl font-semibold leading-tight text-on-dark">{glow!.headline}</p>
          {glow!.headlineSub && <p className="mt-0.5 text-sm text-on-dark-muted">{glow!.headlineSub}</p>}
          <div className="mt-4 flex gap-2">
            <Button onClick={() => setPlaying(true)}>
              <Play className="size-4" aria-hidden /> Play
            </Button>
            <Button variant="ghost" onClick={share} className="text-on-dark hover:bg-dark-soft">
              <Share2 className="size-4" aria-hidden /> Share
            </Button>
          </div>
        </Card>
      ) : (
        <Card className="flex items-center gap-3 p-5">
          <Sparkles className="size-6 shrink-0 text-muted" aria-hidden />
          <div>
            <p className="font-medium text-ink">Your Glow Up is on its way</p>
            <p className="text-sm text-muted">Keep training and logging progress — your recap unlocks as you go.</p>
          </div>
        </Card>
      )}

      <div>
        <p className="mb-2 text-sm font-semibold text-ink-soft">Your goals</p>
        {(goals ?? []).length === 0 ? (
          <div className="rounded-card border border-dashed border-line px-4 py-3 text-sm text-muted">
            No goals yet — ask your gym to set one with you.
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {goals!.map((g) => (
              <GoalRow key={g.id} goal={g} />
            ))}
          </div>
        )}
      </div>

      <SubscriptionsCard />

      {playing && glow && <GlowUpStory wrapped={glow} onClose={() => setPlaying(false)} onShare={share} />}
    </div>
  );
}

const PAYMENT_METHOD_LABELS: Record<MemberSubscription['method'], string> = {
  CASH: 'Cash',
  ECOCASH: 'EcoCash',
  ONEMONEY: 'OneMoney',
  INNBUCKS: 'InnBucks',
  BANK_TRANSFER: 'Bank transfer',
  ONLINE: 'Online',
  OTHER: 'Other',
};

function SubscriptionsCard() {
  const { data, loading } = useAsync(getMySubscriptions);
  const subs = data ?? [];
  const todayIsoV = isoDate(new Date());

  return (
    <div>
      <p className="mb-2 text-sm font-semibold text-ink-soft">My subscriptions</p>
      {loading && subs.length === 0 ? (
        <Skeleton className="h-20 rounded-card" />
      ) : subs.length === 0 ? (
        <div className="rounded-card border border-dashed border-line px-4 py-3 text-sm text-muted">
          No payments yet — your membership and training payments will show here.
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {subs.map((s) => {
            const isPt = s.type === 'PERSONAL_TRAINING';
            const active = s.type === 'MEMBERSHIP' && !!s.periodEnd && s.periodEnd >= todayIsoV;
            return (
              <Card key={s.id} className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex min-w-0 items-center gap-2">
                    <Receipt className="size-4 shrink-0 text-muted" aria-hidden />
                    <p className="truncate font-medium text-ink">{s.planName ?? (isPt ? 'Personal training' : 'Membership')}</p>
                  </div>
                  <p className="shrink-0 font-semibold text-ink">{money(s.amount)}</p>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted">
                  <Badge>{isPt ? 'Personal training' : 'Membership'}</Badge>
                  {active && <Badge tone="accent">Active</Badge>}
                  <span>{PAYMENT_METHOD_LABELS[s.method]}</span>
                  <span>· Paid {formatDate(s.paidOn)}</span>
                </div>
                {s.periodStart && s.periodEnd && (
                  <p className="mt-1.5 text-xs text-muted">
                    Covers {formatDate(s.periodStart)} – {formatDate(s.periodEnd)}
                  </p>
                )}
                {isPt && s.coachName && <p className="mt-1.5 text-xs text-muted">with {s.coachName}</p>}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

function StreakHero({ streak, thisWeek, completed }: { streak: number; thisWeek: number; completed: number }) {
  return (
    <Card tone="dark" className="p-5 text-center">
      <p className="text-eyebrow uppercase text-accent">Day streak</p>
      <div className="mt-1 flex items-center justify-center gap-2">
        <Flame className={cn('size-9', streak > 0 ? 'text-accent' : 'text-on-dark-muted')} aria-hidden />
        <span className="font-display text-6xl font-bold leading-none text-on-dark">{streak}</span>
      </div>
      <p className="mt-2 text-sm text-on-dark-muted">
        {streak > 0 ? 'Keep it alive — train today 🔥' : 'Complete a workout to start your streak'}
      </p>
      <div className="mt-4 flex justify-center gap-8 border-t border-dark-line pt-4">
        <div>
          <p className="font-display text-xl font-semibold text-on-dark">{thisWeek}</p>
          <p className="text-xs text-on-dark-muted">this week</p>
        </div>
        <div>
          <p className="font-display text-xl font-semibold text-on-dark">{completed}</p>
          <p className="text-xs text-on-dark-muted">total done</p>
        </div>
      </div>
    </Card>
  );
}

const MONTH_FMT = new Intl.DateTimeFormat('en', { month: 'long', year: 'numeric' });

function ActivityCalendar({ activeDays }: { activeDays: string[] }) {
  const active = new Set(activeDays);
  const now = new Date();
  const todayIsoV = isoDate(now);
  const [month, setMonth] = useState(() => new Date(now.getFullYear(), now.getMonth(), 1));

  const year = month.getFullYear();
  const m = month.getMonth();
  const startDow = (new Date(year, m, 1).getDay() + 6) % 7; // Monday = 0
  const daysInMonth = new Date(year, m + 1, 0).getDate();
  const cells: (Date | null)[] = [
    ...Array.from({ length: startDow }, () => null),
    ...Array.from({ length: daysInMonth }, (_, i) => new Date(year, m, i + 1)),
  ];
  while (cells.length % 7 !== 0) cells.push(null);

  // Planned workouts for the visible month (assigned by a coach or self-added),
  // so upcoming days are surfaced — not just past training.
  const monthFrom = isoDate(new Date(year, m, 1));
  const monthTo = isoDate(new Date(year, m, daysInMonth));
  const { data: monthWeek } = useAsync(() => getMemberWeek(monthFrom, monthTo), [monthFrom, monthTo]);
  const planned = new Set((monthWeek?.workouts ?? []).map((w) => w.date));

  const trainedThisMonth = cells.filter((d) => d && active.has(isoDate(d))).length;
  const plannedThisMonth = cells.filter((d) => d && !active.has(isoDate(d)) && planned.has(isoDate(d))).length;
  const dow = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

  return (
    <Card className="p-4">
      <div className="mb-3 flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold text-ink-soft">Activity</p>
          <p className="text-xs text-muted">
            {trainedThisMonth} done
            {plannedThisMonth > 0 && ` · ${plannedThisMonth} planned`}
          </p>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            aria-label="Previous month"
            onClick={() => setMonth(new Date(year, m - 1, 1))}
            className="inline-flex size-8 items-center justify-center rounded-control text-muted hover:bg-surface hover:text-ink"
          >
            <ChevronLeft className="size-4" aria-hidden />
          </button>
          <span className="min-w-[7.5rem] text-center text-sm font-medium text-ink">{MONTH_FMT.format(month)}</span>
          <button
            type="button"
            aria-label="Next month"
            onClick={() => setMonth(new Date(year, m + 1, 1))}
            className="inline-flex size-8 items-center justify-center rounded-control text-muted hover:bg-surface hover:text-ink"
          >
            <ChevronRight className="size-4" aria-hidden />
          </button>
        </div>
      </div>

      <div className="mx-auto max-w-xs">
        <div className="grid grid-cols-7 gap-1">
          {dow.map((d, i) => (
            <p key={i} className="pb-1 text-center text-[11px] font-medium text-muted">{d}</p>
          ))}
          {cells.map((d, i) => {
            if (!d) return <span key={i} />;
            const iso = isoDate(d);
            const on = active.has(iso);
            const isPlanned = !on && planned.has(iso);
            const isToday = iso === todayIsoV;
            const future = iso > todayIsoV;
            return (
              <div key={iso} className="flex aspect-square items-center justify-center">
                <span
                  className={cn(
                    'flex size-8 items-center justify-center rounded-full text-sm',
                    on
                      ? 'bg-ok-ink font-semibold text-white'
                      : isPlanned
                        ? 'border border-accent text-ink'
                        : future
                          ? 'text-muted/40'
                          : 'text-ink-soft',
                    isToday && (on ? 'ring-2 ring-accent ring-offset-1 ring-offset-card' : 'ring-1 ring-accent'),
                  )}
                >
                  {d.getDate()}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </Card>
  );
}

function Milestones({ streak, completed }: { streak: number; completed: number }) {
  const streakTiers = [3, 7, 14, 30, 60, 90];
  const totalTiers = [5, 10, 25, 50, 100];
  const nextStreak = streakTiers.find((t) => streak < t);
  const nextTotal = totalTiers.find((t) => completed < t);
  const chips = [
    ...streakTiers.filter((t) => streak >= t).map((t) => ({ label: `${t}-day 🔥`, earned: true })),
    ...(nextStreak ? [{ label: `${nextStreak}-day`, earned: false }] : []),
    ...totalTiers.filter((t) => completed >= t).map((t) => ({ label: `${t} workouts 💪`, earned: true })),
    ...(nextTotal ? [{ label: `${nextTotal} workouts`, earned: false }] : []),
  ];
  if (chips.length === 0) return null;
  const toGo = nextStreak ? nextStreak - streak : 0;
  return (
    <Card className="p-4">
      <p className="mb-2 text-sm font-semibold text-ink-soft">Milestones</p>
      <div className="flex flex-wrap gap-2">
        {chips.map((c, i) => (
          <span
            key={i}
            className={cn(
              'rounded-pill px-3 py-1 text-xs font-medium',
              c.earned ? 'bg-accent text-accent-ink' : 'border border-dashed border-line text-muted',
            )}
          >
            {c.label}
          </span>
        ))}
      </div>
      {nextStreak && (
        <p className="mt-2 text-xs text-muted">
          {toGo} more day{toGo > 1 ? 's' : ''} to your {nextStreak}-day badge.
        </p>
      )}
    </Card>
  );
}

function GoalRow({ goal }: { goal: Goal }) {
  const pct = Math.max(0, Math.min(100, goal.progressPercent ?? 0));
  const unit = goal.unit ? ` ${goal.unit}` : '';
  return (
    <Card className="p-4">
      <div className="flex items-center justify-between gap-2">
        <p className="flex items-center gap-1.5 font-medium text-ink">
          <Target className="size-4 text-muted" aria-hidden /> {goal.title}
        </p>
        {goal.status === 'REACHED' && <Badge>Reached 🎉</Badge>}
      </div>
      <div className="mt-2 h-1.5 w-full overflow-hidden rounded-pill bg-surface">
        <div className="h-full rounded-pill bg-accent-strong transition-all" style={{ width: `${pct}%` }} />
      </div>
      <p className="mt-1.5 text-xs text-muted">
        {goal.currentValue != null ? `${goal.currentValue}${unit}` : '—'}
        {goal.targetValue != null ? ` / ${goal.targetValue}${unit} target` : ''}
        {goal.progressPercent != null ? ` · ${Math.round(goal.progressPercent)}%` : ''}
      </p>
    </Card>
  );
}

function WorkoutCard({
  workout,
  onToggleExercise,
  onMarkDone,
  onDelete,
}: {
  workout: MemberWorkoutView;
  onToggleExercise: (index: number) => void;
  onMarkDone: (completed: boolean) => void;
  onDelete?: () => void;
}) {
  const [open, setOpen] = useState<Set<number>>(new Set());
  const total = workout.exercises.length;
  const doneCount = workout.exercises.filter((e) => e.done).length;
  const pct = total ? Math.round((doneCount / total) * 100) : 0;

  function toggleInstr(i: number) {
    setOpen((p) => {
      const n = new Set(p);
      if (n.has(i)) n.delete(i);
      else n.add(i);
      return n;
    });
  }

  return (
    <Card className={cn('overflow-hidden p-0', workout.completed && 'ring-1 ring-ok-ink/40')}>
      <div className="border-b border-line p-4">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <p className="flex items-center gap-2 font-display text-base font-semibold text-ink">
              {workout.workoutName}
              {workout.kind === 'SELF' && <Badge>Yours</Badge>}
            </p>
            {(workout.timeLabel || workout.openSession) && (
              <p className="mt-0.5 flex items-center gap-2 text-xs text-muted">
                {workout.timeLabel && (
                  <span className="inline-flex items-center gap-1">
                    <Clock className="size-3.5" aria-hidden /> {workout.timeLabel}
                  </span>
                )}
                {workout.openSession && <span>Open session</span>}
              </p>
            )}
          </div>
          {workout.completed && (
            <span className="inline-flex items-center gap-1 rounded-pill bg-ok-soft px-2.5 py-1 text-xs font-semibold text-ok-ink">
              <Check className="size-3.5" aria-hidden /> Done
            </span>
          )}
        </div>
        {total > 0 && (
          <div className="mt-3">
            <div className="h-1.5 w-full overflow-hidden rounded-pill bg-surface">
              <div className="h-full rounded-pill bg-ok-ink transition-all" style={{ width: `${pct}%` }} />
            </div>
            <p className={cn('mt-1.5 text-xs font-medium', workout.completed ? 'text-ok-ink' : 'text-muted')}>
              {momentumMessage(doneCount, total, workout.completed)}
            </p>
          </div>
        )}
      </div>

      <ul className="divide-y divide-line">
        {workout.exercises.map((ex, i) => {
          const hasHow = ex.instructions && ex.instructions.length > 0;
          const isOpen = open.has(i);
          return (
            <li key={i} className="p-3">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  aria-label={ex.done ? 'Mark not done' : 'Mark done'}
                  onClick={() => onToggleExercise(i)}
                  className={cn(
                    'flex size-7 shrink-0 items-center justify-center rounded-full border transition-transform active:scale-90',
                    ex.done ? 'border-ok-ink bg-ok-ink' : 'border-line-strong hover:border-ink',
                  )}
                >
                  {ex.done && <Check className="size-4 animate-pop-in text-white" aria-hidden />}
                </button>
                <div className="size-11 shrink-0 overflow-hidden rounded-control bg-surface">
                  {ex.image ? (
                    <img src={ex.image} alt="" loading="lazy" className="size-full object-cover" />
                  ) : (
                    <div className="flex size-full items-center justify-center text-muted">
                      <Dumbbell className="size-4" aria-hidden />
                    </div>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className={cn('text-sm font-medium', ex.done ? 'text-muted line-through' : 'text-ink')}>{ex.name}</p>
                  <p className="text-sm text-muted">
                    {[ex.sets ? `${ex.sets} sets` : null, ex.reps ? `${ex.reps} reps` : null, ex.load, ex.rest]
                      .filter(Boolean)
                      .join(' · ') || '—'}
                  </p>
                </div>
                {hasHow && (
                  <button
                    type="button"
                    onClick={() => toggleInstr(i)}
                    className="inline-flex items-center gap-0.5 text-xs font-medium text-muted hover:text-ink"
                    aria-expanded={isOpen}
                  >
                    How to <ChevronDown className={cn('size-4 transition-transform', isOpen && 'rotate-180')} aria-hidden />
                  </button>
                )}
              </div>
              {hasHow && isOpen && (
                <ol className="mt-2 ml-10 list-decimal space-y-1 pl-4 text-sm text-ink-soft">
                  {ex.instructions.map((step, s) => (
                    <li key={s}>{step}</li>
                  ))}
                </ol>
              )}
            </li>
          );
        })}
      </ul>

      <div className="flex items-center justify-between gap-2 border-t border-line p-3">
        {onDelete ? (
          <button type="button" onClick={onDelete} className="inline-flex items-center gap-1 text-sm text-muted hover:text-bad-ink">
            <Trash2 className="size-4" aria-hidden /> Delete
          </button>
        ) : (
          <span />
        )}
        <Button size="sm" variant={workout.completed ? 'ghost' : 'primary'} onClick={() => onMarkDone(!workout.completed)}>
          {workout.completed ? 'Mark not done' : 'Mark workout done'}
        </Button>
      </div>
    </Card>
  );
}

interface BuilderItem {
  key: string;
  exerciseId: string;
  exerciseName: string;
  sets: string;
  reps: string;
}

function AddWorkoutModal({ date, onClose, onSaved }: { date: string; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState('');
  const [items, setItems] = useState<BuilderItem[]>([]);
  const [picking, setPicking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function add(ex: Exercise) {
    setItems((p) => [...p, { key: uid(), exerciseId: ex.id, exerciseName: ex.name, sets: '3', reps: '10' }]);
  }
  function patch(key: string, field: 'sets' | 'reps', value: string) {
    setItems((p) => p.map((i) => (i.key === key ? { ...i, [field]: value } : i)));
  }

  async function save() {
    setError(null);
    if (!name.trim()) return setError('Name your workout.');
    if (items.length === 0) return setError('Add at least one exercise.');
    setSaving(true);
    try {
      await createSelfWorkout({
        date,
        name: name.trim(),
        items: items.map((i) => ({ exerciseId: i.exerciseId, sets: i.sets ? Number(i.sets) : null, reps: i.reps.trim() || null })),
      });
      onSaved();
    } catch {
      setError('Could not save. Try again.');
      setSaving(false);
    }
  }

  return (
    <Modal open onClose={onClose}>
      <div className="flex max-h-[82vh] w-full flex-col">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-lg font-semibold text-ink">Add your workout</h2>
          <button type="button" aria-label="Close" onClick={onClose} className="text-muted hover:text-ink">
            <X className="size-5" aria-hidden />
          </button>
        </div>
        <p className="mt-1 text-xs text-muted">Only you can see this — it won't be shared with your gym or studio.</p>

        <div className="mt-3 rounded-control bg-accent-soft px-3 py-2.5 text-xs leading-relaxed text-ink-soft">
          💡 Training solo? A coach keeps your form safe, your programming right, and your progress steady — ask your gym about coaching.
        </div>

        <div className="mt-3">
          <Input label="Workout name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. My push day" />
        </div>

        <div className="mt-3 flex items-center justify-between">
          <p className="text-sm font-medium text-ink">{items.length} exercises</p>
          <Button size="sm" variant="ghost" onClick={() => setPicking(true)}>
            <Plus className="size-4" aria-hidden /> Add exercise
          </Button>
        </div>

        <div className="mt-2 min-h-0 flex-1 overflow-y-auto">
          {items.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted">No exercises yet.</p>
          ) : (
            <ul className="flex flex-col gap-2">
              {items.map((it) => (
                <li key={it.key} className="rounded-control border border-line p-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-ink">{it.exerciseName}</p>
                    <button type="button" aria-label="Remove" onClick={() => setItems((p) => p.filter((x) => x.key !== it.key))} className="text-muted hover:text-bad-ink">
                      <Trash2 className="size-4" aria-hidden />
                    </button>
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <label className="flex flex-col gap-1">
                      <span className="text-xs font-medium text-muted">Sets</span>
                      <input value={it.sets} inputMode="numeric" onChange={(e) => patch(it.key, 'sets', e.target.value)} className="h-9 rounded-control border border-line bg-elevated px-2.5 text-sm outline-none focus:border-ink" />
                    </label>
                    <label className="flex flex-col gap-1">
                      <span className="text-xs font-medium text-muted">Reps</span>
                      <input value={it.reps} placeholder="8-12" onChange={(e) => patch(it.key, 'reps', e.target.value)} className="h-9 rounded-control border border-line bg-elevated px-2.5 text-sm outline-none focus:border-ink" />
                    </label>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {error && <p className="mt-2 text-sm font-medium text-bad-ink">{error}</p>}

        <div className="mt-3 flex justify-end gap-2 border-t border-line pt-3">
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={save} loading={saving}>
            {!saving && <Check className="size-4" aria-hidden />} Save
          </Button>
        </div>
      </div>

      {picking && <MemberExercisePicker onAdd={add} onClose={() => setPicking(false)} />}
    </Modal>
  );
}

function MemberExercisePicker({ onAdd, onClose }: { onAdd: (ex: Exercise) => void; onClose: () => void }) {
  const [all, setAll] = useState<Exercise[]>([]);
  const [groups, setGroups] = useState<string[]>([]);
  const [group, setGroup] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [added, setAdded] = useState<Set<string>>(new Set());

  useEffect(() => {
    getMemberExercises().then(setAll);
    getMemberExerciseFilters().then((f) => setGroups(f.muscleGroups));
  }, []);

  const list = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return all
      .filter((e) => (!group || e.muscleGroups.includes(group)) && (!needle || e.name.toLowerCase().includes(needle)))
      .slice(0, 60);
  }, [all, group, q]);

  return (
    <Modal open onClose={onClose}>
      <div className="flex max-h-[80vh] w-full flex-col">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-lg font-semibold text-ink">Add exercises</h2>
          <button type="button" aria-label="Close" onClick={onClose} className="text-muted hover:text-ink">
            <X className="size-5" aria-hidden />
          </button>
        </div>
        <div className="relative mt-3">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted" aria-hidden />
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search exercises…" className="h-touch w-full rounded-control border border-line bg-elevated pl-9 pr-3 text-sm outline-none focus:border-ink" />
        </div>
        <div className="mt-2 flex flex-wrap gap-1.5">
          <Chip label="All" active={!group} onClick={() => setGroup(null)} />
          {groups.map((g) => (
            <Chip key={g} label={g} active={group === g} onClick={() => setGroup(g)} />
          ))}
        </div>
        <div className="mt-3 min-h-0 flex-1 overflow-y-auto">
          <ul className="flex flex-col divide-y divide-line">
            {list.map((e) => {
              const on = added.has(e.id);
              return (
                <li key={e.id} className="flex items-center gap-3 py-2">
                  <div className="size-11 shrink-0 overflow-hidden rounded-control bg-surface">
                    {e.images[0] ? (
                      <img src={e.images[0]} alt="" loading="lazy" className="size-full object-cover" />
                    ) : (
                      <div className="flex size-full items-center justify-center text-muted">
                        <Dumbbell className="size-4" aria-hidden />
                      </div>
                    )}
                  </div>
                  <p className="min-w-0 flex-1 truncate text-sm font-medium text-ink">{e.name}</p>
                  <Button
                    size="sm"
                    variant={on ? 'ghost' : 'primary'}
                    onClick={() => {
                      onAdd(e);
                      setAdded((p) => new Set(p).add(e.id));
                    }}
                  >
                    {on ? <Check className="size-4" aria-hidden /> : <Plus className="size-4" aria-hidden />}
                    {on ? 'Added' : 'Add'}
                  </Button>
                </li>
              );
            })}
          </ul>
        </div>
        <div className="mt-3 flex justify-end border-t border-line pt-3">
          <Button onClick={onClose}>Done</Button>
        </div>
      </div>
    </Modal>
  );
}

function Chip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn('h-8 rounded-pill px-3 text-xs font-medium transition-colors', active ? 'bg-ink text-on-dark' : 'bg-surface text-ink-soft hover:bg-line')}
    >
      {label}
    </button>
  );
}
